'use client'

import type { CarouselTitle, CarouselVariants } from '@fukku/carousel/types'
import { ClientCrossSelling } from 'cross-selling/components/shared/CrossSelling/client/ClientCrossSelling'
import { CROSS_SELLING_MAX_VIEW_ITEMS } from 'cross-selling/constants/constants'
import type { CrossSellingPersonalizationServiceResponse } from 'cross-selling/services/getPersonalization/getPersonalization'
import type { CrossSellingServiceResponse } from 'cross-selling/types'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import type { ProductImageType } from 'product/types'
import type { ProductLocations } from 'product/types/ProductLocations'

export interface PersonalizationCrossSellingProps {
	readonly dataTestId: string
	readonly excludedProducts: CrossSellingServiceResponse[]
	readonly personalizedProducts: CrossSellingPersonalizationServiceResponse[]
	readonly title: string
	readonly location: ProductLocations
	readonly showDiscount?: boolean
	readonly variant?: CarouselVariants
}

const imageSortCriteria: ProductImageType[] = ['B']

export function PersonalizationCrossSelling({
	dataTestId,
	excludedProducts,
	personalizedProducts,
	title,
	location,
	showDiscount,
	variant,
}: PersonalizationCrossSellingProps): React.ReactNode {
	const { t } = useLabels()
	const titleProps: CarouselTitle = {
		content: t(title),
	}

	const filteredPersonalizedProducts = personalizedProducts
		.filter((personalizedProduct) => {
			const isSameProductFn = (
				comparedProduct: CrossSellingServiceResponse
			): boolean =>
				personalizedProduct.productId === comparedProduct.productId &&
				personalizedProduct.colorId === comparedProduct.colorId

			const isOnExcludedProducts = excludedProducts?.find((excludedProduct) =>
				isSameProductFn(excludedProduct)
			)

			return !isOnExcludedProducts
		})
		.slice(0, CROSS_SELLING_MAX_VIEW_ITEMS)

	return (
		filteredPersonalizedProducts?.length > 0 && (
			<ClientCrossSelling
				crossSellingItems={filteredPersonalizedProducts}
				dataTestId={dataTestId}
				imageSortCriteria={imageSortCriteria}
				title={titleProps}
				location={location}
				productListId={location}
				hasFavoriteButton
				showDiscount={showDiscount}
				variant={variant}
			/>
		)
	)
}
